import { autoinject } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { Toaster } from 'app/shared/lib/toaster';
import { apiCall } from 'app/shared/lib/essette-api';
import { AdmissionCareDayService } from 'app/um/services/admission-care-day-service';
import { AuthService } from 'app/um/services/auth-service';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import * as UM from 'app/um/models/essette-api';

@autoinject()
export class SaveRevenueCodeMapping extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    public isEdit: boolean = true;

    protected providers: UM.ProviderLocation[] = [];
    protected levelsOfCare: UM.LevelOfCare[] = [];
    protected filteredLevelsOfCare: UM.LevelOfCare[] = [];

    @bindable.objectNull({ changeHandler: 'filterLevelsOfCare' })
    public selectedProvider?: UM.ProviderLocation;
    public selectedLevelOfCare?: UM.LevelOfCare;
    public revenueCode: string = '';

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly toaster: Toaster,
        private readonly admissionCareDayService: AdmissionCareDayService,
        private readonly authService: AuthService,
        ...rest: any[]) {
        super(...rest);
    }

    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    public async reload(): Promise<void> {
        await this.loadLookups();
        await this.filterLevelsOfCare();
    }

    public reset(): void {
        this.selectedLevelOfCare = undefined;
        this.selectedProvider = undefined;
        this.revenueCode = '';
    }

    public async submit(): Promise<boolean> {
        return this.saveRevenueCodeMapping();
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async filterLevelsOfCare(): Promise<void> {
        this.filteredLevelsOfCare = this.levelsOfCare;
        if(this.selectedProvider?.id && !this.isEdit) {

            const queryResult = await this.admissionCareDayService.loadProviderLocationByIdWithLevelsOfCare(this.selectedProvider.id, {});
            if(queryResult.count == 1) {
                const selectedProviderLocation = queryResult.results[0];
                const providerLevelOfCareIds = new Set();
                selectedProviderLocation.levelsOfCare?.forEach(pLoc => providerLevelOfCareIds.add(pLoc.id));

                this.filteredLevelsOfCare = this.levelsOfCare.filter(loc => !providerLevelOfCareIds.has(loc.id))
                if(providerLevelOfCareIds.has(this.selectedLevelOfCare?.id)) {
                    this.selectedLevelOfCare = undefined;
                }
            }
        }
    }

    private buildLevelOfCareWithDetail(): UM.LevelOfCare {
        return {
            id: this.selectedLevelOfCare?.id,
            providerLocations: [{
                id: this.selectedProvider?.id,
                detail: {
                    revenueCode: this.revenueCode
                }
            }]
        };
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadLookups(): Promise<void> {
        this.state.beginLoad();
        try {
            const options: Partial<IQueryLoadOptions> = { all: true };

            const [providers, levelsOfCare] = await Promise.all([
                this.authService.loadCommonProvidersWithAdmission(options, ["providerLocation"]),
                this.admissionCareDayService.loadLevelsOfCare(options),
            ]);

            const foundIds = new Set<string>();
            const grouped: UM.ProviderLocation[] = [];

            providers.results.forEach(({ providerLocation }) => {
                if(!providerLocation?.id || foundIds.has(providerLocation.id)) return;

                grouped.push(providerLocation);
                foundIds.add(providerLocation.id);
            });

            this.providers = grouped;
            this.levelsOfCare = levelsOfCare.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveRevenueCodeMapping(): Promise<boolean> {
        let isSuccessful = false;
        try {
            this.state.beginSave();

            const levelOfCareWithDetail = this.buildLevelOfCareWithDetail();

            const response = this.isEdit
                ? await this.admissionCareDayService.updateLevelsOfCareDetail(levelOfCareWithDetail)
                : await this.admissionCareDayService.insertLevelsOfCareDetail(levelOfCareWithDetail);

            isSuccessful = response.isSuccessful;

        } catch(error) {
            throw error;
        } finally {
            this.toaster.standard.saved('um:auth.levelOfCare.revenueCodeMapping.$', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }
}
